import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './big-number-calculator.component.module.scss';
import { CalcResultBar } from '../calc-result-bar.component';

type Op = 'add' | 'sub' | 'mul' | 'div' | 'mod' | 'pow';

const OP_LABEL: Record<Op, string> = {
    add: '+', sub: '−', mul: '×', div: '÷', mod: '%', pow: '^',
};

interface State {
    a: string;
    b: string;
    op: Op;
    error: string | null;
    copied: boolean;
}

export default class BigNumberCalculatorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        a: '999999999999999999999999999999',
        b: '111111111111111111111111111111',
        op: 'mul',
        error: null,
        copied: false,
    };

    private get result(): string {
        const { a, b, op } = this.state;
        try {
            const aBig = BigInt(a.trim());
            const bBig = BigInt(b.trim());
            switch (op) {
                case 'add': return (aBig + bBig).toString();
                case 'sub': return (aBig - bBig).toString();
                case 'mul': return (aBig * bBig).toString();
                case 'div':
                    if (bBig === 0n) return 'Error: divide by zero';
                    return (aBig / bBig).toString();
                case 'mod':
                    if (bBig === 0n) return 'Error: modulo by zero';
                    return (aBig % bBig).toString();
                case 'pow':
                    if (bBig < 0n) return 'Error: negative exponent';
                    if (bBig > 100000n) return 'Error: exponent too large (max 100,000)';
                    return (aBig ** bBig).toString();
            }
        } catch (e) {
            return 'Error: invalid integer input';
        }
    }

    copyOutput = () => {
        const r = this.result;
        if (r.startsWith('Error')) return;
        navigator.clipboard.writeText(r).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { a, b, op, copied } = this.state;
        const result = this.result;
        const isError = result.startsWith('Error');
        return (
            <>
                <CalcResultBar />
                <div className="row">
                <div className="col-12">
                    <div className={styles.calc}>
                        <label className={sharedStyles.label}>
                            A
                            <input className={styles.input} type="text" value={a}
                                spellCheck={false}
                                onChange={e => this.setState({ a: e.target.value, copied: false })} />
                        </label>
                        <label className={sharedStyles.label}>
                            Operation
                            <div className={styles.opRow}>
                                {(Object.keys(OP_LABEL) as Op[]).map(o => (
                                    <button key={o}
                                        className={`${styles.opBtn} ${op === o ? styles.opBtnActive : ''}`}
                                        onClick={() => this.setState({ op: o, copied: false })}
                                        aria-pressed={op === o}>
                                        {OP_LABEL[o]}
                                    </button>
                                ))}
                            </div>
                        </label>
                        <label className={sharedStyles.label}>
                            B
                            <input className={styles.input} type="text" value={b}
                                spellCheck={false}
                                onChange={e => this.setState({ b: e.target.value, copied: false })} />
                        </label>
                    </div>

                    <label className={sharedStyles.label}>Result</label>
                    <div className={`${styles.resultBox} ${isError ? styles.resultError : ''}`}>
                        <code>{result}</code>
                        <span className={styles.length}>{!isError && `${result.replace(/^-/, '').length} digits`}</span>
                        {/* CalcCopyButton couldn't find a .resultValue inside this box (we use
                            <code> instead because the value is arbitrarily long), so it
                            silently no-op'd — kept showing "📋 Copy result" but clicks
                            didn't copy anything. Use the explicit copy button below which
                            calls copyOutput() directly. */}
                        <div className={styles.actions} style={{ marginTop: '0.75rem' }}>
                            <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyOutput} disabled={isError}>
                                {copied ? '✓ Copied' : '📋 Copy result'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </>
        );
    }
}
